import type { JSX } from 'react';

import { IconButton, Tooltip, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useTranslation } from 'react-i18next';

import type { PlaceFieldType } from '@/pages/orders/usePlaceRecordCopy';

// The „vykopírovat celý řádek z Adresáře" control that sits LEFT of each of the
// two place fields in the jízda detail (user request 2026-09-18). Visually the
// same `.iconbtn` as the Adresář row action (`PlacesPage`): 44×44 tap target,
// 8px radius, outline copy glyph that flips to a check for 2 s — so the two
// copy affordances read as one control, wherever the dispatcher meets them.
//
// Enabled for EVERY role, including the ones that see the field itself
// disabled: copying is read-only and is precisely what a dispatcher needs the
// jízda detail for. Disabled — never hidden — while the field is blank, so the
// row's shape does not jump as text is typed. That blank field is the ONLY
// disabled state: the copy is a pure in-memory write of the record the detail
// already carries (`usePlaceRecordCopy`), so there is no in-flight state and
// deliberately no spinner — the user's „navíc se mi to točí a nefunguje"
// (2026-09-18) was exactly that spinner.

const TAP_TARGET_MIN_PX = 44;

export interface PlaceRecordCopyButtonProps {
  field: PlaceFieldType;
  // The field's current text — blank disables the button.
  text: string;
  copied: boolean;
  onCopy: (field: PlaceFieldType) => void;
  'data-testid'?: string;
}

export const PlaceRecordCopyButton = ({
  field,
  text,
  copied,
  onCopy,
  'data-testid': dataTestId,
}: PlaceRecordCopyButtonProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const label = t(copied ? 'editor.copyPlace.done' : 'editor.copyPlace.aria');
  const disabled = text.trim().length === 0;

  return (
    // `span` wrapper: MUI tooltips need a non-disabled child to receive the
    // hover/focus events, and the button is disabled on a blank field.
    <Tooltip title={label}>
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          onClick={() => onCopy(field)}
          disabled={disabled}
          aria-label={label}
          data-testid={dataTestId}
          data-copied={copied ? 'true' : 'false'}
          sx={{
            width: TAP_TARGET_MIN_PX,
            height: TAP_TARGET_MIN_PX,
            borderRadius: 1, // 8px — prototype `.iconbtn`
            color: copied ? theme.palette.primary.main : theme.palette.text.secondary,
            bgcolor: copied ? theme.palette.primary.light : 'transparent',
            '&:hover': {
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.main,
            },
          }}
        >
          {copied ? (
            <CheckIcon fontSize="small" />
          ) : (
            <ContentCopyOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
